import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Navigation from "@/components/Navigation";
import { 
  Loader2, 
  Globe,
  Zap,
  Shield,
  DollarSign,
  ArrowLeftRight,
  Clock,
  User,
  CreditCard,
  AlertCircle,
  CheckCircle2,
  ArrowDown,
  Sparkles,
  Link as LinkIcon,
  Copy,
  Check,
  Mail,
  Send,
  MessageSquare,
  Share2,
  KeyRound,
  Banknote
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Supported currencies for on-ramp
const FIAT_CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'CHF'];

// Crypto options for routing (stablecoins preferred for remittance)
const CRYPTO_OPTIONS = [
  { code: 'USDT', name: 'Tether (USDT)', network: 'Polygon' },
  { code: 'USDC', name: 'USD Coin (USDC)', network: 'Polygon' },
  { code: 'DAI', name: 'Dai (DAI)', network: 'Ethereum' }
];

export default function SendMoneyPage({ user, onLogout }) {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [serviceStatus, setServiceStatus] = useState({ guardarian: null, triplea: null });
  
  // Form state
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [cryptoCurrency, setCryptoCurrency] = useState('USDT');
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  
  // Pre-filled transfer from voice assistant (for direct payment)
  const [prefilledTransfer, setPrefilledTransfer] = useState(null);
  
  // Estimate state
  const [estimate, setEstimate] = useState(null);
  const [feeInfo, setFeeInfo] = useState(null);
  
  // AI Routing state
  const [aiRoute, setAiRoute] = useState(null);
  const [routePriority, setRoutePriority] = useState('balanced');
  
  // Terms acceptance
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  // MTCN Transfer state
  const [mtcnResult, setMtcnResult] = useState(null);
  const [showMtcnSuccess, setShowMtcnSuccess] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [messageCopied, setMessageCopied] = useState(false);

  useEffect(() => {
    checkServiceStatus();
  }, []);

  // Handle prefill from Voice Assistant navigation
  useEffect(() => {
    if (location.state?.prefill) {
      const prefill = location.state.prefill;
      console.log('Prefill data received:', prefill);
      
      // Pre-fill form fields
      if (prefill.amount) setAmount(prefill.amount.toString());
      if (prefill.currency) setCurrency(prefill.currency);
      if (prefill.recipient) setRecipientName(prefill.recipient);
      
      // If skipToPayment is true and we have a wallet address, go directly to payment
      if (prefill.skipToPayment && prefill.walletAddress) {
        setPrefilledTransfer({
          wallet_address: prefill.walletAddress,
          amount: prefill.amount,
          currency: prefill.currency,
          recipient_name: prefill.recipient
        });
        
        toast.info('Transfer details loaded. Click "Proceed to Payment" to complete.');
      }
    }
    
    // Handle transfer result from Voice Assistant
    if (location.state?.showTransferResult && location.state?.transferData) {
      const data = location.state.transferData;
      console.log('Transfer result from voice assistant:', data);
      
      setMtcnResult(data);
      setShowMtcnSuccess(true);
      
      toast.success('Transfer created! Save your claim code below.');
    }
  }, [location.state]);

  useEffect(() => {
    // Get estimate when amount changes
    const timer = setTimeout(() => {
      if (amount && parseFloat(amount) >= 10) {
        getEstimate();
        getAiRoute();
      } else {
        setEstimate(null);
        setFeeInfo(null);
        setAiRoute(null);
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [amount, currency, cryptoCurrency, routePriority]);

  const checkServiceStatus = async () => {
    try {
      const [guardarianRes, tripleaRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/guardarian/health`),
        fetch(`${BACKEND_URL}/api/triplea/health`)
      ]);
      
      const guardarian = await guardarianRes.json();
      const triplea = await tripleaRes.json();
      
      setServiceStatus({ guardarian, triplea });
    } catch (error) {
      console.error('Error checking service status:', error);
    }
  };

  const getAiRoute = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/routing/optimal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: cryptoCurrency,
          amount: parseFloat(amount),
          priority: routePriority
        })
      });
      
      const data = await res.json();
      
      if (data.success) {
        setAiRoute(data);
      }
    } catch (error) {
      console.error('Error getting AI route:', error);
    }
  };

  const getEstimate = async () => {
    setEstimateLoading(true);
    try {
      // Get Guardarian estimate (fiat -> crypto)
      const estimateRes = await fetch(`${BACKEND_URL}/api/guardarian/estimate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fiat_currency: currency,
          fiat_amount: parseFloat(amount),
          crypto_currency: cryptoCurrency
        })
      });
      
      const estimateData = await estimateRes.json();
      
      // Get RouteFlow fee
      const feeRes = await fetch(`${BACKEND_URL}/api/guardarian/calculate-fee?amount=${amount}`);
      const feeData = await feeRes.json();
      
      if (estimateData.success) {
        setEstimate(estimateData);
        setFeeInfo(feeData);
      }
    } catch (error) {
      console.error('Error getting estimate:', error);
    } finally {
      setEstimateLoading(false);
    }
  };

  const handleStartTransfer = async () => {
    // Validation
    if (!amount || parseFloat(amount) < 10) {
      toast.error('Minimum amount is 10 ' + currency);
      return;
    }
    
    if (!recipientName.trim()) {
      toast.error('Please enter recipient name');
      return;
    }
    
    if (!termsAccepted) {
      toast.error('Please accept the Terms of Service to continue');
      return;
    }
    
    setLoading(true);
    
    try {
      // MTCN FLOW: Create transfer with 12-word claim code
      const mtcnRes = await fetch(`${BACKEND_URL}/api/mtcn/create-transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: parseFloat(amount),
          currency: currency,
          sender_email: user?.email || 'sender@routeflowai.com',
          sender_name: user?.full_name || 'RouteFlow User',
          recipient_last_name: recipientName.split(' ').pop() || recipientName, // Use last name as security answer
          message: `Transfer to ${recipientName}`,
          send_email_backup: true // Send backup to sender's email
        })
      });
      
      const mtcnData = await mtcnRes.json();
      
      if (mtcnData.success) {
        // Save the MTCN result
        setMtcnResult({
          ...mtcnData,
          recipient_name: recipientName,
          amount: parseFloat(amount),
          currency: currency
        });
        
        // Show success screen with claim code
        setShowMtcnSuccess(true);
        
        // Save to localStorage for tracking
        const transfers = JSON.parse(localStorage.getItem('pending_transfers') || '[]');
        transfers.push({
          type: 'mtcn',
          transaction_id: mtcnData.transaction_id,
          wallet_address: mtcnData.wallet_address,
          amount: parseFloat(amount),
          currency: currency,
          recipient_name: recipientName,
          status: 'pending_payment',
          created_at: new Date().toISOString()
        });
        localStorage.setItem('pending_transfers', JSON.stringify(transfers));
        
        toast.success('Transfer created! Save the claim code and complete payment.');
        
      } else {
        toast.error(mtcnData.detail || 'Failed to create transfer');
      }
    } catch (error) {
      console.error('Error creating transfer:', error);
      toast.error('Failed to start transfer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // MTCN Share Functions
  const getShareMessage = () => {
    if (!mtcnResult) return '';
    const securityAnswer = recipientName.split(' ').pop() || recipientName;
    const claimUrl = `${window.location.origin}/claim-mtcn`;
    return `🎉 You've received ${mtcnResult.currency} ${mtcnResult.amount} via RouteFlow AI!

To claim your funds:
1. Visit: ${claimUrl}
2. Enter this code: ${mtcnResult.mnemonic}
3. Security answer: ${securityAnswer}
4. Withdraw directly to your bank account

Questions? Visit ${window.location.origin}`;
  };

  const copyClaimCode = () => {
    if (mtcnResult?.mnemonic) {
      navigator.clipboard.writeText(mtcnResult.mnemonic);
      setCodeCopied(true);
      toast.success('Claim code copied!');
      setTimeout(() => setCodeCopied(false), 3000);
    }
  };

  const copyShareMessage = () => {
    const message = getShareMessage();
    navigator.clipboard.writeText(message);
    setMessageCopied(true);
    toast.success('Message copied to clipboard!');
    setTimeout(() => setMessageCopied(false), 3000);
  };

  const shareViaWhatsApp = () => {
    const message = encodeURIComponent(getShareMessage());
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  const shareViaSMS = () => {
    const message = encodeURIComponent(getShareMessage());
    window.open(`sms:?body=${message}`, '_blank');
  };

  const shareViaEmailMTCN = () => {
    const subject = encodeURIComponent(`You received ${mtcnResult?.currency} ${mtcnResult?.amount} from ${user?.full_name || 'a friend'}!`);
    const body = encodeURIComponent(getShareMessage());
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  const proceedToPayment = async () => {
    // Create Guardarian transaction to fund the MTCN wallet
    try {
      setLoading(true);
      const guardarianRes = await fetch(`${BACKEND_URL}/api/guardarian/create-transaction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fiat_currency: mtcnResult.currency,
          fiat_amount: mtcnResult.amount,
          crypto_currency: 'USDC',
          payout_address: mtcnResult.wallet_address, // MTCN wallet address
          email: user?.email || 'guest@routeflowai.com',
          user_id: user?.id,
          recipient_name: mtcnResult.recipient_name
        })
      });
      
      const guardarianData = await guardarianRes.json();
      
      if (guardarianData.success && guardarianData.redirect_url) {
        toast.success('Redirecting to payment...');
        window.open(guardarianData.redirect_url, '_blank');
      } else {
        // Show more specific error message
        const errorMsg = guardarianData.detail || guardarianData.error || 'Payment service unavailable';
        console.error('Guardarian error:', errorMsg);
        
        if (errorMsg.includes('api-key') || errorMsg.includes('token')) {
          toast.error('Payment service configuration issue. Please contact support.');
        } else {
          toast.error(`Payment failed: ${errorMsg}`);
        }
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Payment service unavailable. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const startNewTransfer = () => {
    setShowMtcnSuccess(false);
    setMtcnResult(null);
    setAmount('');
    setRecipientName('');
    setRecipientEmail('');
    setEstimate(null);
  };

  const isServicesReady = serviceStatus.guardarian?.success && serviceStatus.triplea?.success;

  // MTCN Success Screen
  if (showMtcnSuccess && mtcnResult) {
    // Get security answer from mtcnResult (for voice transfers) or from form (for manual transfers)
    const securityAnswer = mtcnResult.recipient_last_name || 
                           mtcnResult.recipient?.split(' ').pop() || 
                           recipientName.split(' ').pop() || 
                           recipientName || 
                           'N/A';
    return (
      <div className="min-h-screen pb-20 md:pb-0 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        <Navigation user={user} onLogout={onLogout} />
        
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto">
            {/* Success Header */}
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-10 h-10 text-emerald-600" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Transfer Created Successfully!
              </h1>
              <p className="text-gray-600">
                Share the claim code below with {mtcnResult.recipient_name}
              </p>
            </div>

            {/* Transfer Summary */}
            <Card className="mb-6 border-emerald-200 bg-emerald-50">
              <CardContent className="pt-6">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Amount</span>
                  <span className="text-2xl font-bold text-emerald-600">
                    {mtcnResult.currency} {mtcnResult.amount.toFixed(2)}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Claim Code Card */}
            <Card className="mb-6 border-2 border-amber-300 bg-amber-50">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-amber-800">
                  <KeyRound className="w-5 h-5" />
                  Claim Code (Save This!)
                </CardTitle>
                <CardDescription className="text-amber-700">
                  This code is shown only once and sent to your email as backup
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-white p-4 rounded-lg border border-amber-200 mb-4">
                  <code className="text-sm sm:text-base font-mono text-gray-800 break-all leading-relaxed">
                    {mtcnResult.mnemonic}
                  </code>
                </div>
                <Button 
                  onClick={copyClaimCode}
                  variant="outline"
                  className="w-full border-amber-400 text-amber-700 hover:bg-amber-100"
                >
                  {codeCopied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  {codeCopied ? 'Copied!' : 'Copy Claim Code'}
                </Button>
              </CardContent>
            </Card>

            {/* Claim Link */}
            <Card className="mb-6">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-gray-800">
                  <LinkIcon className="w-5 h-5" />
                  Claim Link
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-100 p-3 rounded-lg mb-4">
                  <code className="text-sm text-emerald-600 break-all">
                    {window.location.origin}/claim-mtcn
                  </code>
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Security Answer:</strong> {securityAnswer}
                </p>
              </CardContent>
            </Card>

            {/* Wallet Address - For Guardarian payment */}
            <Card className="mb-6 border-blue-200 bg-blue-50">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-blue-800">
                  <CreditCard className="w-5 h-5" />
                  Payment Wallet Address
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-blue-700 mb-3">
                  If Guardarian asks for a wallet address, use this:
                </p>
                <div className="bg-white p-3 rounded-lg border border-blue-200 mb-3">
                  <code className="text-sm text-gray-800 break-all font-mono">
                    {mtcnResult.wallet_address}
                  </code>
                </div>
                <Button 
                  onClick={() => {
                    navigator.clipboard.writeText(mtcnResult.wallet_address);
                    toast.success('Wallet address copied!');
                  }}
                  variant="outline"
                  className="w-full border-blue-400 text-blue-700 hover:bg-blue-100"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Wallet Address
                </Button>
                <p className="text-xs text-blue-600 mt-2">
                  This is the claim wallet. USDC will be sent here for the recipient to claim.
                </p>
              </CardContent>
            </Card>

            {/* Shareable Message */}
            <Card className="mb-6">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-gray-800">
                  <Share2 className="w-5 h-5" />
                  Share with Recipient
                </CardTitle>
                <CardDescription>
                  Copy or share this message with {mtcnResult.recipient_name}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-50 p-4 rounded-lg border mb-4 text-sm text-gray-700 whitespace-pre-line">
                  {getShareMessage()}
                </div>
                
                {/* Share Buttons */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <Button 
                    onClick={copyShareMessage}
                    variant="outline"
                    className="flex items-center justify-center gap-2"
                  >
                    {messageCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {messageCopied ? 'Copied!' : 'Copy Message'}
                  </Button>
                  <Button 
                    onClick={shareViaWhatsApp}
                    className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700"
                  >
                    <MessageSquare className="w-4 h-4" />
                    WhatsApp
                  </Button>
                  <Button 
                    onClick={shareViaEmailMTCN}
                    variant="outline"
                    className="flex items-center justify-center gap-2"
                  >
                    <Mail className="w-4 h-4" />
                    Email
                  </Button>
                  <Button 
                    onClick={shareViaSMS}
                    variant="outline"
                    className="flex items-center justify-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    SMS
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Payment Instructions & Button */}
            <Card className="mb-6 border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 to-teal-50">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-emerald-800">
                  <AlertCircle className="w-5 h-5" />
                  Before You Proceed to Payment
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mb-4">
                  {/* Step 1 */}
                  <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-emerald-200">
                    <div className="w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center text-sm font-bold shrink-0">1</div>
                    <div>
                      <p className="font-medium text-gray-800">Save the Claim Code</p>
                      <p className="text-sm text-gray-600">Copy or save the 12-word claim code above. Share it with {mtcnResult.recipient_name} so they can claim the funds.</p>
                      <p className="text-xs text-emerald-600 mt-1">✓ Also sent to your email as backup</p>
                    </div>
                  </div>
                  
                  {/* Step 2 */}
                  <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-blue-200">
                    <div className="w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold shrink-0">2</div>
                    <div>
                      <p className="font-medium text-gray-800">Copy the Wallet Address</p>
                      <p className="text-sm text-gray-600">You may need to paste this in Guardarian's payment page.</p>
                      <div className="mt-2 flex items-center gap-2">
                        <code className="text-xs bg-blue-50 px-2 py-1 rounded border border-blue-200 font-mono truncate max-w-[200px]">
                          {mtcnResult.wallet_address}
                        </code>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            navigator.clipboard.writeText(mtcnResult.wallet_address);
                            toast.success('Wallet address copied!');
                          }}
                          className="shrink-0 border-blue-400 text-blue-700"
                        >
                          <Copy className="w-3 h-3 mr-1" />
                          Copy
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Step 3 */}
                  <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-200">
                    <div className="w-7 h-7 rounded-full bg-gray-600 text-white flex items-center justify-center text-sm font-bold shrink-0">3</div>
                    <div>
                      <p className="font-medium text-gray-800">Complete Payment</p>
                      <p className="text-sm text-gray-600">Pay with your card. Select <strong>Polygon</strong> network if asked. USDC will be sent to the claim wallet.</p>
                    </div>
                  </div>
                </div>
                
                <Button 
                  onClick={proceedToPayment}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-lg py-6"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <CreditCard className="w-5 h-5 mr-2" />
                  )}
                  Proceed to Payment
                </Button>
              </CardContent>
            </Card>

            {/* Warning */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-800">Important!</p>
                  <p className="text-sm text-red-700">
                    The claim code is NOT stored by RouteFlow AI. If lost, check your email for the backup. 
                    Without the code, funds cannot be recovered.
                  </p>
                </div>
              </div>
            </div>

            {/* New Transfer Button */}
            <Button 
              onClick={startNewTransfer}
              variant="outline"
              className="w-full"
            >
              Start New Transfer
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20 md:pb-0 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <Navigation user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Send Money Internationally
            </h1>
            <p className="text-gray-600">
              Fast, low-cost transfers powered by blockchain technology
            </p>
          </div>

          {/* Prefilled Transfer - Skip to Payment Card */}
          {prefilledTransfer && (
            <Card className="mb-6 border-2 border-emerald-500 bg-emerald-50">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-emerald-800">
                  <CheckCircle2 className="w-5 h-5" />
                  Transfer Ready for Payment
                </CardTitle>
                <CardDescription>
                  Your transfer from Voice Assistant is ready. Complete the payment below.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-white p-4 rounded-lg mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Amount:</span>
                    <span className="font-bold text-xl text-emerald-600">
                      {prefilledTransfer.currency} {prefilledTransfer.amount}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Recipient:</span>
                    <span className="font-medium">{prefilledTransfer.recipient_name}</span>
                  </div>
                </div>
                <Button 
                  onClick={async () => {
                    // Direct payment for prefilled transfer
                    try {
                      setLoading(true);
                      const guardarianRes = await fetch(`${BACKEND_URL}/api/guardarian/create-transaction`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          fiat_currency: prefilledTransfer.currency,
                          fiat_amount: prefilledTransfer.amount,
                          crypto_currency: 'USDC',
                          payout_address: prefilledTransfer.wallet_address,
                          email: user?.email || 'guest@routeflowai.com',
                          user_id: user?.id,
                          recipient_name: prefilledTransfer.recipient_name
                        })
                      });
                      
                      const guardarianData = await guardarianRes.json();
                      
                      if (guardarianData.success && guardarianData.redirect_url) {
                        toast.success('Redirecting to payment...');
                        window.open(guardarianData.redirect_url, '_blank');
                      } else {
                        toast.error('Failed to create payment. Please try again.');
                      }
                    } catch (error) {
                      console.error('Payment error:', error);
                      toast.error('Payment failed. Please try again.');
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-lg py-6"
                >
                  {loading ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...</>
                  ) : (
                    <><CreditCard className="w-5 h-5 mr-2" /> Proceed to Payment</>
                  )}
                </Button>
                <Button 
                  onClick={() => setPrefilledTransfer(null)}
                  variant="ghost"
                  className="w-full mt-2"
                >
                  Create New Transfer Instead
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Service Status Banner */}
          {!isServicesReady && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800">Service Initializing</p>
                  <p className="text-sm text-amber-600">
                    Please wait while we connect to payment providers...
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Transfer Card */}
          <Card className="shadow-xl border-0">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2">
                <ArrowLeftRight className="w-5 h-5 text-emerald-600" />
                International Transfer
              </CardTitle>
              <CardDescription>
                Send money to family and friends worldwide
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* You Send Section */}
              <div className="space-y-2">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Banknote className="w-4 h-4 text-emerald-600" />
                  You Send
                </Label>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="text-2xl h-14"
                      min="10"
                      max="50000"
                      data-testid="amount-input"
                    />
                  </div>
                  <Select value={currency} onValueChange={setCurrency}>
                    <SelectTrigger className="w-28 h-14" data-testid="currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FIAT_CURRENCIES.map(cur => (
                        <SelectItem key={cur} value={cur}>{cur}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {parseFloat(amount) > 0 && parseFloat(amount) < 10 && (
                  <p className="text-sm text-red-500">Minimum amount is 10 {currency}</p>
                )}
              </div>

              {/* Arrow */}
              <div className="flex justify-center">
                <div className="p-2 bg-emerald-100 rounded-full">
                  <ArrowDown className="w-5 h-5 text-emerald-600" />
                </div>
              </div>

              {/* AI Routing Section */}
              <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border border-emerald-100">
                <Label className="text-sm font-semibold flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-emerald-600" />
                  AI-Optimized Route
                </Label>
                
                {/* Priority Selection */}
                <div className="flex gap-2 mb-4">
                  {[
                    { value: 'cost', label: 'Cheapest', icon: DollarSign },
                    { value: 'balanced', label: 'Balanced', icon: ArrowLeftRight },
                    { value: 'speed', label: 'Fastest', icon: Zap }
                  ].map(({ value, label, icon: Icon }) => (
                    <button
                      key={value}
                      onClick={() => setRoutePriority(value)}
                      className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        routePriority === value 
                          ? 'bg-emerald-600 text-white' 
                          : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {label}
                    </button>
                  ))}
                </div>

                <div className="flex items-center justify-between">
                  <Select value={cryptoCurrency} onValueChange={setCryptoCurrency}>
                    <SelectTrigger className="w-48" data-testid="crypto-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CRYPTO_OPTIONS.map(crypto => (
                        <SelectItem key={crypto.code} value={crypto.code}>
                          {crypto.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="text-right">
                    {estimateLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin text-emerald-600" />
                    ) : estimate ? (
                      <div>
                        <p className="text-lg font-semibold text-emerald-700">
                          ~{parseFloat(estimate.estimated_crypto_amount).toFixed(2)} {cryptoCurrency}
                        </p>
                        <p className="text-xs text-gray-500">Estimated conversion</p>
                      </div>
                    ) : null}
                  </div>
                </div>

                {/* AI Route Details */}
                {aiRoute?.optimal_route && (
                  <div className="mt-4 p-3 bg-white rounded-lg border border-emerald-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        Best Network: {aiRoute.optimal_route.network_name}
                      </span>
                      <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">
                        Score: {aiRoute.optimal_route.scores.total.toFixed(0)}/100
                      </span>
                    </div>
                    
                    {/* Primary Metrics */}
                    <div className="grid grid-cols-3 gap-2 text-xs text-gray-600 mb-2">
                      <div className="flex items-center gap-1">
                        <DollarSign className="w-3 h-3" />
                        <span>~${aiRoute.optimal_route.cost_usd.toFixed(4)} total</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>{aiRoute.optimal_route.estimated_time_display}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Shield className="w-3 h-3" />
                        <span>{aiRoute.optimal_route.scores.reliability}% reliable</span>
                      </div>
                    </div>
                    
                    {/* Congestion & Slippage Indicators */}
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
                        aiRoute.optimal_route.congestion_level === 'low' 
                          ? 'bg-green-100 text-green-700' 
                          : aiRoute.optimal_route.congestion_level === 'medium'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        <Globe className="w-3 h-3" />
                        <span>{aiRoute.optimal_route.congestion_level} congestion</span>
                      </div>
                      {aiRoute.optimal_route.expected_slippage_percent !== undefined && (
                        <div className="flex items-center gap-1 px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs">
                          <span>{aiRoute.optimal_route.expected_slippage_percent}% slippage</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Market Conditions */}
                    {aiRoute.market_conditions && (
                      <div className={`p-2 rounded text-xs mb-2 ${
                        aiRoute.market_conditions.state === 'favorable' 
                          ? 'bg-green-50 text-green-700'
                          : aiRoute.market_conditions.state === 'moderate'
                          ? 'bg-yellow-50 text-yellow-700'
                          : 'bg-orange-50 text-orange-700'
                      }`}>
                        <span className="font-medium">Market: </span>
                        {aiRoute.market_conditions.advice}
                      </div>
                    )}
                    
                    {/* Timing Recommendation */}
                    {aiRoute.optimal_route.timing_recommendation && (
                      <p className="text-xs text-gray-500 italic">
                        {aiRoute.optimal_route.timing_recommendation}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Fee Breakdown */}
              {feeInfo && estimate && (
                <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Transfer Amount</span>
                    <span>{parseFloat(amount).toFixed(2)} {currency}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">RouteFlow Fee ({feeInfo.service_fee_percentage}%)</span>
                    <span>{feeInfo.service_fee.toFixed(2)} {currency}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>Provider fees included in rate</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between font-semibold">
                    <span>Recipient Gets (approx.)</span>
                    <span className="text-emerald-600">
                      ~{(parseFloat(amount) - feeInfo.service_fee - 2).toFixed(2)} {currency}
                    </span>
                  </div>
                </div>
              )}

              {/* Recipient Section */}
              <div className="space-y-4 border-t pt-6">
                <div className="flex items-center gap-2 text-gray-700 mb-4">
                  <User className="w-5 h-5 text-emerald-600" />
                  <span className="font-semibold">Recipient Details</span>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="recipient-name">Recipient Full Name *</Label>
                  <Input
                    id="recipient-name"
                    placeholder="John Smith"
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                    data-testid="recipient-name-input"
                  />
                  <p className="text-xs text-gray-500">
                    The recipient's last name will be used as the security answer to claim funds.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="recipient-email" className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-emerald-600" />
                    Recipient Email (optional)
                  </Label>
                  <Input
                    id="recipient-email"
                    type="email"
                    placeholder="recipient@email.com"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    data-testid="recipient-email-input"
                  />
                  <p className="text-xs text-gray-500">
                    For your records only. You are responsible for sharing the claim code with the recipient.
                  </p>
                </div>
              </div>

              {/* How It Works */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-3 flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  How Your Transfer Works
                </h4>
                <div className="space-y-2 text-sm text-blue-700">
                  <div className="flex items-center gap-2">
                    <span className="bg-blue-200 text-blue-800 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0">1</span>
                    <span>You pay {currency} and receive a 12-word claim code</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="bg-blue-200 text-blue-800 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0">2</span>
                    <span>Share the claim code and link with the recipient</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="bg-blue-200 text-blue-800 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0">3</span>
                    <span>Recipient enters code + security answer to claim funds</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="bg-blue-200 text-blue-800 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0">4</span>
                    <span>Recipient withdraws directly to their bank account</span>
                  </div>
                </div>
              </div>

              {/* Terms Checkbox */}
              <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg border">
                <input 
                  type="checkbox" 
                  id="transfer-terms-checkbox" 
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  data-testid="transfer-terms-checkbox"
                />
                <label htmlFor="transfer-terms-checkbox" className="text-sm text-gray-600 leading-tight">
                  I agree to the <Link to="/terms" target="_blank" className="text-emerald-600 hover:underline font-medium">Terms of Service</Link> and 
                  <Link to="/privacy" target="_blank" className="text-emerald-600 hover:underline font-medium ml-1">Privacy Policy</Link>. 
                  I understand that RouteFlow AI is a technology interface and all payment processing is handled by licensed partners (Guardarian, Triple-A).
                </label>
              </div>

              {/* Submit Button */}
              <Button 
                onClick={handleStartTransfer}
                disabled={loading || !isServicesReady || !amount || parseFloat(amount) < 10 || !recipientName.trim() || !termsAccepted}
                className="w-full h-14 text-lg bg-emerald-600 hover:bg-emerald-700"
                data-testid="submit-transfer-btn"
              >
                {loading ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...</>
                ) : (
                  <><LinkIcon className="w-5 h-5 mr-2" /> Create Transfer</>
                )}
              </Button>

              {/* Trust badges */}
              <div className="flex justify-center gap-6 pt-4 text-xs text-gray-500">
                <div className="flex items-center gap-1">
                  <Shield className="w-4 h-4" />
                  <span>Non-Custodial</span>
                </div>
                <div className="flex items-center gap-1">
                  <Zap className="w-4 h-4" />
                  <span>Minutes, Not Days</span>
                </div>
                <div className="flex items-center gap-1">
                  <DollarSign className="w-4 h-4" />
                  <span>0.48% Fee</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Provider Info */}
          <div className="mt-6 text-center text-xs text-gray-500">
            <p>
              On-ramp powered by <span className="font-medium">Guardarian</span> • MTCN secure transfer system
            </p>
            <p className="mt-1">
              RouteFlow AI is a technology interface. All financial services provided by licensed partners.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
